import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { faRotateLeft } from '@fortawesome/pro-solid-svg-icons';
import classNames from 'classnames';
import { CSSProperties, ComponentProps, ReactNode, useState } from 'react';
import { FormattedMessage, FormattedNumber } from 'react-intl';
import styled from 'styled-components';

import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { Boost } from '@sorare/core/src/atoms/icons/Boost';
import { Horizontal } from '@sorare/core/src/atoms/layout/flex';
import { fantasy, glossary } from '@sorare/core/src/lib/glossary';

import { useIsBoostAffordable } from 'hooks/useIsBoostAffordable';

import { CountdownDialog } from './CountdownDialog';
import { BoostButton_anyCard } from './__generated__/index.graphql';

const StyledBoost = styled(Boost)`
  &.withOpacity {
    opacity: 0.5;
  }
`;
const StyledSpan = styled.span`
  font-weight: normal !important;
  color: var(--c-nd-700);
`;
const CooldownButton = styled(Button)`
  isolation: isolate;
  &.hasCooldown {
    overflow: hidden;
    color: var(--c-nd-600);

    > * {
      z-index: 1;
    }

    &:before {
      content: '';
      position: absolute;
      inset: 0;
      width: var(--cooldown-percent);
      background: var(--c-nd-50);
    }
  }
`;

type Props = {
  anyCard: BoostButton_anyCard;
  selected?: boolean;
  balanceUsed?: number;
} & Pick<ComponentProps<typeof Button>, 'size' | 'onClick'>;

export const BoostButton = ({
  anyCard,
  selected,
  balanceUsed,
  size,
  onClick,
}: Props) => {
  const [open, setOpen] = useState(false);
  const isBoostAffordable = useIsBoostAffordable();
  const {
    affordable,
    xpNeeded,
    hasCooldown,
    cooldownMessage,
    cooldownPercent,
  } = isBoostAffordable(anyCard, balanceUsed);

  const disabled = !selected && !affordable && !hasCooldown;
  const isMaxLevel = anyCard.xpNeededForNextGrade === null;

  let label: ReactNode;
  let startIcon: ReactNode = (
    <StyledBoost
      size={2}
      rarity={disabled ? undefined : anyCard.rarity}
      className={classNames({ withOpacity: hasCooldown })}
    />
  );
  if (selected) {
    label = (
      <Horizontal gap={0.5}>
        <FormattedMessage
          {...fantasy.undoNumber}
          values={{
            value: xpNeeded,
            span: chunks => <StyledSpan>{chunks}</StyledSpan>,
          }}
        />
      </Horizontal>
    );
    startIcon = <FontAwesomeIcon icon={faRotateLeft} />;
  } else if (hasCooldown) {
    label = cooldownMessage;
  } else if (isMaxLevel) {
    label = <FormattedMessage {...glossary.max} />;
    startIcon = null;
  } else {
    label = <FormattedNumber value={xpNeeded} />;
  }

  return (
    <>
      <CooldownButton
        color={hasCooldown ? 'tertiary' : 'quaternary'}
        size={size}
        onClick={hasCooldown ? () => setOpen(true) : onClick}
        disabled={disabled}
        startIcon={startIcon}
        style={
          {
            '--cooldown-percent': `${(cooldownPercent || 0) * 100}%`,
          } as CSSProperties
        }
        className={classNames('tertiary', { hasCooldown })}
      >
        {label}
      </CooldownButton>
      {open && anyCard.nextLevelUpAvailableAt && (
        <CountdownDialog
          open
          onClose={() => setOpen(false)}
          date={anyCard.nextLevelUpAvailableAt}
        />
      )}
    </>
  );
};

BoostButton.fragments = {
  anyCard: gql`
    fragment BoostButton_anyCard on AnyCardInterface {
      slug
      rarity: rarityTyped
      xpNeededForNextGrade
      lastLeveledUpAt
      nextLevelUpAvailableAt
      ...useIsBoostAffordable_anyCard
    }
    ${useIsBoostAffordable.fragments.anyCard}
  ` as TypedDocumentNode<BoostButton_anyCard>,
};
